import axios from "axios";
import { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import ReactPaginate from "react-paginate";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import XlsxPopulate from "xlsx-populate/browser/xlsx-populate";

const SupplierBuyLead = () => {
  const Navigate = useNavigate();
  const location = useLocation();
  const [showImagesModal, setShowImagesModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [monthFilter, setMonthFilter] = useState("");
  const monthQuery = monthFilter ? `&month=${monthFilter}` : "";
  const request = localStorage.getItem("request");

  const [buyerId, setBuyerId] = useState(() => {
    return localStorage.getItem("buyer_id");
  });

  const handleRowClick = (id) => {
    const newId = buyerId === id ? null : id;
    setBuyerId(newId);

    if (newId) {
      localStorage.removeItem("buyer_id");
      localStorage.removeItem("request");
    } else {
      localStorage.removeItem("buyer_id");
    }
  };

  const highlightedUserIds =
    typeof location.state?.user_id === "string"
      ? location.state.user_id.split(",").map((id) => id.trim())
      : [];

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const userType = user?.user_type;

  const isAlertShown = useRef(false);
  useEffect(() => {
    if (location.state && !isAlertShown.current) {
      toast.success(location.state);
      isAlertShown.current = true;
      window.history.replaceState({}, null);
    }
  }, [location.state]);

  const [data, setData] = useState([]);
  const [totalpage, setTotalPage] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [userId, setUserId] = useState(0);
  const [usersId, setMupliUserId] = useState([]);

  const SupplierLeadData = () => {
    const params = new URLSearchParams();

    // ✅ always include page + limit
    params.append("page", page);
    params.append("limit", 200);

    // ✅ add filters if they exist
    if (search) params.append("search", search);
    if (monthFilter) params.append("month", monthFilter);

    axios
      .get(`${process.env.REACT_APP_URL}/admin/orders?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("Token")}`,
        },
      })
      .then((res) => {
        setTotalPage(res?.data?.data?.pages || 1);

        // ✅ all orders
        const allOrders = res?.data?.data?.orders || [];

        // ✅ only supplier & offline_supplier orders
        const supplierOrders = allOrders.filter(
          (order) =>
            order?.order_type === "supplier" ||
            order?.order_type === "offline_supplier"
        );

        // ✅ group by user_id (supplier)
        const grouped = supplierOrders.reduce((acc, order) => {
          const uid = order?.user?.user_id;
          if (!uid) return acc;

          if (!acc[uid]) {
            acc[uid] = {
              user: order.user,
              orders: [],
              // keep track of the latest order date for sorting
              latest_created_at: order.created_at,
            };
          }

          acc[uid].orders.push(order);

          // update latest_created_at if this order is newer
          if (
            !acc[uid].latest_created_at ||
            new Date(order.created_at) > new Date(acc[uid].latest_created_at)
          ) {
            acc[uid].latest_created_at = order.created_at;
          }

          return acc;
        }, {});

        // ✅ convert object to array for rendering
        let groupedArray = Object.values(grouped);

        // ✅ sort latest first
        groupedArray.sort(
          (a, b) =>
            new Date(b.latest_created_at) - new Date(a.latest_created_at)
        );

        // ✅ optional: sort each supplier’s orders newest-first
        groupedArray.forEach((supplier) => {
          supplier.orders.sort(
            (a, b) => new Date(b.created_at) - new Date(a.created_at)
          );
        });

        setData(groupedArray);
      })
      .catch((err) => {
        console.error(
          "Supplier Lead fetch failed:",
          err.response?.data || err.message
        );
        if (err?.response?.data?.message === "Unauthorized!") {
          Navigate("/");
        }
      });
  };

  const [selectedUsers, setSelectedUsers] = useState([]);

  const handleCheckboxChange = (userId) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const exportToExcel = async () => {
    try {
      if (!data || data.length === 0) {
        alert("No data available to export.");
        return;
      }

      // ✅ Step 1: Group orders by user_id
      const groupedData = Object.values(
        data.reduce((acc, order) => {
          const user = order.user || {};
          const userId = user.user_id;

          if (!acc[userId]) {
            acc[userId] = {
              user,
              orders: [],
            };
          }

          acc[userId].orders.push(order);
          return acc;
        }, {})
      );

      // ✅ Step 2: Map grouped users into export rows
      const exportData = groupedData.map((group, index) => {
        const user = group.user || {};
        const orders = group.orders || [];

        // Find the latest order based on created_at
        const latestOrder =
          orders.length > 0
            ? orders.reduce((latest, current) =>
                new Date(current.created_at) > new Date(latest.created_at)
                  ? current
                  : latest
              )
            : null;

        return {
          "Sr. No.": index + 1,
          Name: user.name || "",
          Email: user.email || "",
          Phone: user.phone || "",
          Company: user.company_name || "",
          // "Total Orders": orders.length,
          // "Latest Product": latestOrder?.title || "",
          // "Latest Quantity": latestOrder?.quantity || "",
          // "Order Status": latestOrder?.order_status || "",
          "Created At": latestOrder?.created_at
            ? new Date(latestOrder.created_at).toLocaleDateString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })
            : "",
        };
      });

      if (exportData.length === 0) {
        alert("No valid records found.");
        return;
      }

      // 🔹 Create base sheet
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Supplier Buy Lead");

      // 🔹 Column width optimization
      worksheet["!cols"] = Object.keys(exportData[0]).map((key) => ({
        wch: Math.max(15, key.length + 2),
      }));

      // 🔹 Convert to array for XlsxPopulate
      const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const xpop = await XlsxPopulate.fromDataAsync(wbout);
      const sheet = xpop.sheet("Supplier Buy Lead");

      // 🎨 Style header row (blue, white text, centered)
      const headerRow = sheet.range(
        "A1:" + sheet.usedRange().endCell().columnName() + "1"
      );
      headerRow.style({
        bold: true,
        horizontalAlignment: "center",
        verticalAlignment: "center",
        fill: "1B55E2", // Blue header
        fontColor: "FFFFFF",
        border: true,
        fontFamily: "Calibri",
      });

      // 🎨 Apply zebra striping & borders
      const totalRows = sheet.usedRange().endCell().rowNumber();
      for (let i = 2; i <= totalRows; i++) {
        const row = sheet.range(
          `A${i}:${sheet.usedRange().endCell().columnName()}${i}`
        );
        row.style({
          border: true,
          horizontalAlignment: "left",
          verticalAlignment: "center",
          fontFamily: "Calibri",
          fontSize: 11,
        });
        if (i % 2 === 0) row.style({ fill: "F7F7F7" }); // light gray alternate rows
      }

      // 📋 Freeze top row and enable filters
      sheet.freezePanes(1, 0);
      sheet.autoFilter();

      // 🧾 Export final Excel
      const blob = await xpop.outputAsync();
      saveAs(
        new Blob([blob], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        `supplier-buy-leads_${new Date().toISOString().split("T")[0]}.xlsx`
      );

      toast.success("Excel exported successfully!");
    } catch (err) {
      console.error("Export failed:", err);
      toast.error("Failed to export data.");
    }
  };

  const handleDownloadFormat = async () => {
    try {
      const headers = [
        "name",
        "email",
        "phone",
        "user_type",
        "company_name",
        "website",
        "gst_number",
        "business_type",
        "designation",
        "address",
        "city",
        "state",
        "country",
        "pincode",
        "product_id",
        "title",
        "quantity",
        "size",
        "material_color",
        "print_coverage",
        "product_description",
        "remark",
      ];

      const sampleRow = [
        "John Doe",
        "john@example.com",
        "9876543210",
        "buyer",
        "ABC Packaging",
        "https://abcpack.com",
        "27AAAPL1234C1ZV",
        "Retail",
        "Manager",
        "21 MG Road",
        "Mumbai",
        "Maharashtra",
        "India",
        "400001",
        "101",
        "Corrugated Box",
        "100",
        "10x10x5",
        "Brown",
        "Full",
        "Premium quality corrugated packaging box",
        "Sample remark",
      ];

      const wsData = [headers, sampleRow];

      // 🔹 Create workbook + worksheet
      const worksheet = XLSX.utils.aoa_to_sheet(wsData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "BuyerOrders");

      // 🔹 Column widths
      worksheet["!cols"] = headers.map((h) => ({
        wch: Math.max(15, h.length + 2),
      }));

      // 🔹 Export file
      const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });

      const blob = new Blob([wbout], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      saveAs(blob, "supplier-order-import-format.xlsx");
      toast.success("Format downloaded successfully!");
    } catch (error) {
      console.error("Download failed:", error);
      toast.error("Failed to generate format file.");
    }
  };
  const fileInputRef = useRef(null);

  const handleImportClick = () => {
    // reset so selecting the same file triggers onChange
    if (fileInputRef.current) fileInputRef.current.value = null;
    fileInputRef.current?.click();
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const binaryStr = event.target.result;
        const workbook = XLSX.read(binaryStr, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const rows = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

        if (!rows.length) {
          toast.error("Excel file is empty");
          return;
        }

        const apiUrl = process.env.REACT_APP_URL;
        const token = localStorage.getItem("Token");

        let successCount = 0;
        let failCount = 0;

        const loadingId = toast.loading("Importing, please wait...");

        // Use entries() to get row index for clearer error toasts
        for (const [i, row] of rows.entries()) {
          const rowNum = i + 2; // +2 → account for header row in Excel
          const who =
            row.name || row.company_name || row.email || row.phone || "Unnamed";
          const prodHint = row.title || row.product_id || "";

          try {
            const registerPayload = {
              name: row.name || "",
              email: row.email || "",
              phone: row.phone || "",
              user_type: "offline_supplier",
              company_name: row.company_name || "",
              website: row.website || "",
              gst_number: row.gst_number || "",
              business_type: row.business_type || "",
              designation: row.designation || "",
              address: row.address || "",
              city: row.city || "",
              state: row.state || "",
              country: row.country || "",
              pincode: row.pincode || "",
              status: "active",
            };

            const additionalFields = [
              { label: "Material Color", value: row.material_color },
              { label: "Print Coverage", value: row.print_coverage },
            ].filter((f) => f.value);

            const orderPayload = {
              product_id: row.product_id || "",
              title: row.title || "",
              quantity: row.quantity || "1",
              product_description: row.product_description || "",
              size: row.size || "",
              material_color: row.material_color || "",
              print_coverage: row.print_coverage || "",
              remark: row.remark || "",
              order_type: "offline_supplier",
              additional_fields: additionalFields,
              user_data: registerPayload,
            };

            const res = await axios.post(
              `${apiUrl}/admin/orders/import`,
              orderPayload,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                },
              }
            );

            if (res?.data?.success) {
              successCount++;
              toast.success(`Row ${rowNum} ✓ Imported: ${who}`);
            } else {
              failCount++;
              const msg = res?.data?.message || "Unknown error";
              toast.error(
                `Row ${rowNum} ✗ ${who}${
                  prodHint ? ` • ${prodHint}` : ""
                }: ${msg}`
              );
            }
          } catch (err) {
            failCount++;
            const msg =
              err.response?.data?.message ||
              err.response?.data?.error ||
              err.message ||
              "Unknown error";
            // This shows backend messages like "Product not found or inactive"
            toast.error(
              `Row ${rowNum} ✗ ${who}${
                prodHint ? ` • ${prodHint}` : ""
              }: ${msg}`
            );
          }
        }

        toast.dismiss(loadingId);

        // Final summary
        if (failCount === 0) {
          toast.success(
            `✅ All ${successCount} records imported successfully!`
          );
        } else if (successCount > 0) {
          toast.error(
            `⚠️ ${successCount} imported, ${failCount} failed. See error toasts above.`
          );
        } else {
          toast.error("❌ All records failed to import.");
        }

        SupplierLeadData();
      } catch (err) {
        // If anything goes wrong outside the loop
        const msg =
          err.response?.data?.message || err.message || "Import failed";
        toast.error(`🚨 ${msg}`);
      } finally {
        // ensure next click works even with the same file
        if (fileInputRef.current) fileInputRef.current.value = null;
      }
    };

    reader.readAsBinaryString(file);
  };

  const handlePageClick = (e) => {
    setPage(e.selected + 1);
  };

  const handleWhatsAppClick = (phoneNumber) => {
    window.open(`https://wa.me/${phoneNumber}`, "_blank");
  };

  const handleCallClick = (phoneNumber) => {
    window.location.href = `tel:${phoneNumber}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  };

  useEffect(() => {
    SupplierLeadData();
  }, [page, search, monthFilter, request, selectedUsers]);

  const IMG_BASE = process.env.REACT_APP_IMAGE_URL;
  const initialsOf = (name = "") =>
    name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
  const TINTS = [
    { bg: "var(--color-info-bg)", color: "var(--color-info-text)" },
    { bg: "var(--color-success-bg)", color: "var(--color-success-text)" },
    { bg: "var(--color-violet-bg)", color: "var(--color-violet-text)" },
    { bg: "var(--color-warning-bg)", color: "var(--color-warning-text)" },
  ];
  const tint = (s = "") => TINTS[(String(s).length) % TINTS.length];

  const [dense, setDense] = useState(false);
  const [sortDir, setSortDir] = useState("");
  const [isMobileView, setIsMobileView] = useState(typeof window !== "undefined" && window.innerWidth < 768);
  useEffect(() => {
    const h = () => setIsMobileView(window.innerWidth < 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  const rowName = (item) => item?.name || item?.user?.name || "";
  const rowEmail = (item) => item?.email || item?.user?.email || "";
  const rowPhone = (item) => item?.phone || item?.user?.phone || "";
  // `logo_img` is not a column — the field is `logo_image`, so this always
  // returned undefined and every row fell through to the empty placeholder.
  const rowLogo = (item) =>
    item?.profile_image ||
    item?.user?.profile_image ||
    item?.logo_image ||
    item?.user?.logo_image;

  let displayData = [...(data || [])];
  if (sortDir) {
    displayData.sort((a, b) => {
      const av = rowName(a).toLowerCase();
      const bv = rowName(b).toLowerCase();
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }

  const LogoCell = ({ item }) => {
    const logo = rowLogo(item);
    if (logo) {
      return (
        <button type="button" className="al-thumb" title="View logo" style={{ padding: 0, cursor: "pointer" }}
          onClick={(e) => { e.stopPropagation(); setSelectedImage(`${IMG_BASE}${logo}`); setShowImagesModal(true); }}>
          <img src={`${IMG_BASE}${logo}`} alt="Logo" />
        </button>
      );
    }
    return (
      <button type="button" className="al-thumb" title="No logo" style={{ cursor: "pointer" }}
        onClick={(e) => { e.stopPropagation(); setSelectedImage(null); setShowImagesModal(true); }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 4-6 8-6s8 2 8 6"/></svg>
      </button>
    );
  };

  const OrdersCell = ({ item }) => {
    const n = item?.orders?.length || 0;
    const has = n > 0;
    const hot = highlightedUserIds.includes(String(item?.id));
    return (
      <button type="button" title="View products / orders"
        onClick={(e) => { e.stopPropagation(); Navigate(`/admin/supplier-offline-product-list/${item?.user?.user_id}`); }}
        style={{
          display: "inline-flex", alignItems: "center", gap: 7, height: 30, padding: "0 10px",
          borderRadius: 9, cursor: "pointer", fontSize: 12, fontWeight: 700,
          border: `1px solid ${has || hot ? "transparent" : "var(--color-border)"}`,
          background: has || hot ? "var(--color-primary-weak)" : "var(--color-surface-2)",
          color: has || hot ? "var(--color-primary)" : "var(--color-text-tertiary)",
        }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
        <span>{has ? `View ${n} order${n === 1 ? "" : "s"}` : "No orders"}</span>
      </button>
    );
  };

  const RowActions = ({ item }) => (
    <div className="al-actions">
      <button type="button" className="al-act" title="Call" style={{ color: "#34B7F1" }}
        onClick={(e) => { e.stopPropagation(); handleCallClick(rowPhone(item)); }}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.79 19.79 0 0 1 3.1 5.18 2 2 0 0 1 5 3h3a2 2 0 0 1 2 1.72 12 12 0 0 0 .56 2.57 2 2 0 0 1-.45 2.11L9 11a16 16 0 0 0 5 5l1.6-1.1a2 2 0 0 1 2.1-.45 12 12 0 0 0 2.57.56A2 2 0 0 1 22 16.92z"/></svg>
      </button>
      <button type="button" className="al-act" title="WhatsApp" style={{ color: "#25D366" }}
        onClick={(e) => { e.stopPropagation(); handleWhatsAppClick(rowPhone(item)); }}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7A8.38 8.38 0 014 11.5a8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z"/></svg>
      </button>
    </div>
  );

  const GRID = { gridTemplateColumns: "1.35fr 1.6fr 1fr 64px 1.2fr 96px" };

  return (
    <div className="al">
      <Toaster position="top-center" reverseOrder={false} />

      <div className="al-head">
        <div>
          <h1 className="al-title">Supplier Buy Lead</h1>
          <p className="al-sub">Buy leads submitted by suppliers looking to source materials.</p>
        </div>
        <div className="al-head__actions">
          {userType === "admin" && (
            <>
              <button type="button" className="al-btn" onClick={handleDownloadFormat}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                Download Format
              </button>
              <button type="button" className="al-btn" onClick={handleImportClick}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                Import
              </button>
              <input type="file" id="fileInput" accept=".csv,.xlsx,.xls" style={{ display: "none" }} ref={fileInputRef} onChange={handleFileUpload} />
              <button type="button" className="al-btn" onClick={exportToExcel}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                Export
              </button>
            </>
          )}
          <button type="button" className="al-btn al-btn--primary"
            onClick={() => Navigate("/admin/supplier-fake/add", { state: { usertype: "supplier" } })}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Add
          </button>
        </div>
      </div>

      <div className="al-card">
        <div className="al-toolbar">
          <div className="al-search">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-tertiary)" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/></svg>
            <input type="search" placeholder="Search buy leads by supplier, product…"
              value={search}
              onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="al-select">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            <select value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)}>
              <option value="">Select Month</option>
              <option value="All">All</option>
              <option value="1">Last 1 Month</option>
              <option value="3">Last 3 Months</option>
              <option value="6">Last 6 Months</option>
            </select>
          </div>
          <button type="button" className="al-tool" style={{ marginLeft: "auto" }} onClick={() => setDense((d) => !d)} title="Toggle density">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
            {dense ? "Comfortable" : "Density"}
          </button>
        </div>

        {!isMobileView ? (
          <div className="al-tablewrap">
            <div style={{ minWidth: 940, "--al-row-pad": dense ? "8px" : "13px" }}>
              <div className="al-thead" style={GRID}>
                <span className="al-th al-th--sort" onClick={() => setSortDir((d) => (d === "asc" ? "desc" : d === "desc" ? "" : "asc"))}>
                  Name
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: sortDir ? 1 : 0.45, transform: sortDir === "desc" ? "rotate(180deg)" : "none" }}><polyline points="18 15 12 9 6 15"/></svg>
                </span>
                <span className="al-th">Email</span>
                <span className="al-th">Mobile Number</span>
                <span className="al-th">Logo</span>
                <span className="al-th">Orders</span>
                <span className="al-th al-th--right">Action</span>
              </div>

              {displayData && displayData.length > 0 ? (
                displayData.map((item, index) => {
                  const t = tint(rowName(item));
                  const hot = selectedUsers.includes(item?.id) || buyerId === String(item?.id);
                  return (
                    <div className="al-row" style={{ ...GRID, background: hot ? "var(--color-primary-weak)" : undefined, cursor: "pointer" }} key={index}
                      onClick={() => handleRowClick(item?.id)}>
                      <div className="al-id">
                        <span className="al-avatar" style={{ background: t.bg, color: t.color }}>{initialsOf(rowName(item))}</span>
                        <span className="al-name">{rowName(item)}</span>
                      </div>
                      <span className="al-muted al-ellipsis" title={rowEmail(item)}>{rowEmail(item)}</span>
                      <span className="al-mono al-muted">{rowPhone(item)}</span>
                      <span><LogoCell item={item} /></span>
                      <span><OrdersCell item={item} /></span>
                      <RowActions item={item} />
                    </div>
                  );
                })
              ) : (
                <div className="al-empty">
                  <div className="al-empty__icon">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
                  </div>
                  <p className="al-empty__title">No Record Found</p>
                  <p className="al-empty__text">Supplier buy leads will appear here.</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="al-mobile">
            {displayData && displayData.length > 0 ? (
              displayData.map((item, index) => {
                const t = tint(rowName(item));
                const hot = selectedUsers.includes(item?.id) || buyerId === String(item?.id);
                return (
                  <div className="al-mcard" key={index} style={{ background: hot ? "var(--color-primary-weak)" : undefined }}
                    onClick={() => handleRowClick(item?.id)}>
                    <div className="al-mcard__top">
                      <span className="al-avatar" style={{ background: t.bg, color: t.color }}>{initialsOf(rowName(item))}</span>
                      <div className="al-name" style={{ flex: 1 }}>{rowName(item)}</div>
                      <LogoCell item={item} />
                    </div>
                    <div className="al-mrow"><span className="al-muted">Email</span><span>{rowEmail(item)}</span></div>
                    <div className="al-mrow"><span className="al-muted">Mobile</span><span className="al-mono">{rowPhone(item)}</span></div>
                    <div className="al-mrow"><span className="al-muted">Orders</span><OrdersCell item={item} /></div>
                    <div className="al-mactions"><RowActions item={item} /></div>
                  </div>
                );
              })
            ) : (
              <div className="al-empty"><p className="al-empty__title">No Record Found</p></div>
            )}
          </div>
        )}

        <div className="al-foot">
          <span className="al-foot__info">Showing <strong>{displayData?.length || 0}</strong> buy leads</span>
          {data?.length !== 0 && (
            <ReactPaginate
              previousLabel={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>}
              nextLabel={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>}
              breakLabel={"..."}
              pageCount={totalpage}
              marginPagesDisplayed={2}
              pageRangeDisplayed={2}
              onPageChange={handlePageClick}
              containerClassName={"pagination justify-content-end m-0"}
              pageClassName={"page-item"} pageLinkClassName={"page-link"}
              previousClassName={"page-item"} previousLinkClassName={"page-link"}
              nextClassName={"page-item"} nextLinkClassName={"page-link"}
              breakClassName={"page-item"} breakLinkClassName={"page-link"}
              activeClassName={"active"} />
          )}
        </div>
      </div>

      {showImagesModal && (
        <div className="al-modal" onClick={() => setShowImagesModal(false)}>
          <div className="al-modal__scrim" />
          <div className="al-modal__box" onClick={(e) => e.stopPropagation()} style={{ width: "min(340px,92vw)" }}>
            <div className="al-modal__head">
              <h3 className="al-modal__title">Logo Preview</h3>
              <button type="button" className="al-act" onClick={() => setShowImagesModal(false)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div style={{ padding: 20, display: "flex", justifyContent: "center" }}>
              {selectedImage ? (
                <img src={selectedImage} alt="Preview" style={{ maxWidth: "100%", maxHeight: 260, borderRadius: 12 }} />
              ) : (
                <p className="al-muted">No image available</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierBuyLead;
